import { Component, Emit, Prop, Watch } from 'vue-property-decorator'
import GridHeader from '@/components/GridHeader.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import {
    BillingModalEntityGridState,
    initialBillingModalEntityGridState
} from '@/modules/billing-module/shared/components/modal-entity/components/grid/billing-modal-entity-grid.model'
import { BillingModalEntityGridDatasource } from '@/modules/billing-module/shared/components/modal-entity/components/grid/config/billing-modal-entity-grid.datasource'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { CommonExportType, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { AxiosResponse } from 'axios'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { ReportGridRequest } from '@/api/services/common.dto'
import { GridUtils } from '@/shared/util/grid-utils'

@Component({
    components: {
        GridHeader,
        GridDisplayOptions,
        VtGrid
    }
})
export default class BillingModalEntityGrid<T> extends BaseCrudServerSideComponent<T> implements BaseCrud {
    @Prop({ type: Object, required: true }) readonly datasource!: BillingModalEntityGridDatasource<T>
    @Prop({ type: String, default: '' }) readonly gridUiComponent!: string
    @Prop({ type: String, default: '' }) readonly gridTitle!: string
    @Prop({ type: Boolean, default: false }) readonly selectionMode!: boolean
    @Prop({ type: Function, default: () => [] }) readonly getExternalContextMenuItems!: (params: GetContextMenuItemsParams) => MenuItemDef[]

    @Emit()
    totalCountChanged(totalCount: number): number {
        return totalCount
    }
    @Emit()
    selectedRowsChanged(selectedRows: T[]): T[] {
        return selectedRows
    }

    @Watch('_rowCount')
    private _onRowCountChanged(): void {
        this.totalCountChanged(this._rowCount)
    }
    @Watch('state.selectedRows')
    private onSelectedRowsChanged(): void {
        this.selectedRowsChanged(this.state.selectedRows) // emits an event
    }

    state: BillingModalEntityGridState<T>
    constructor() {
        super()
        this.state = initialBillingModalEntityGridState()
    }

    /// GRID COMPUTED ///

    private get _rowCount(): number {
        return this.state.gridApi?.paginationGetRowCount() ?? 0
    }
    get gridSubtitle(): string {
        if (this.selectionMode && this.state.selectedRows.length) {
            const choice = this.state.selectedRows.length > 1 ? 2 : 1
            return `${this.state.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
        }
        return ''
    }

    /// VUE HOOKS ///

    mounted(): void {
        this.state.gridUiComponent = this.gridUiComponent
        if (this.datasource) {
            this.state.datasource = this.datasource
            this.state.columnsDef = this.datasource.columnDefs
        }
    }

    /// AG GRID HOOKS ///

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        const menuItems: MenuItemDef[] = []
        if (this.getExternalContextMenuItems) {
            menuItems.push(...this.getExternalContextMenuItems(params))
        }
        return menuItems
    }

    /// GRID OPERATIONS ///

    onClearFilter(): void {
        this.state.gridApi?.setFilterModel(null)
    }

    async exportReport(exportType: CommonExportType): Promise<AxiosResponse<Blob> | null> {
        const columns = this.getColumnsToExport()
        if (columns && this.state.gridApi && this.state.datasource?.exporter) {
            const entityIdentifiersRequest: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map((selectedRow: any) => selectedRow.id)
            }
            const request: ReportGridRequest = {
                sectionLabel: this.gridTitle,
                columns,
                entityIdentifiersRequest,
                filterModel: this.state.gridApi.getFilterModel()
            }
            const params: ExportCommonParams = {
                sort: GridUtils.getSortValue(this.state.gridApi.getSortModel()),
                exportType
            }
            return this.state.datasource.exporter(request, params)
        }
        return null
    }
}
